import {
  useState, useCallback, useRef, useEffect,
} from 'react';
import {
  Box, Slider, Typography, IconButton,
} from '@mui/material';
import { PlayCircleOutline, PauseCircleOutline } from '@mui/icons-material';
import { styled } from '@mui/system';
import { theme } from '../../lib/index';

function AudioPlayer({ src }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [position, setPosition] = useState(0);

  const audioPlayer = useRef();
  const progressBar = useRef();
  const animationRef = useRef();

  useEffect(() => {
    if (Number.isNaN(audioPlayer.current.duration)) {
      setDuration(0);
    } else {
      setDuration(audioPlayer.current.duration);
    }
  }, [audioPlayer?.current?.loadedmetadata, audioPlayer?.current?.readyState]);

  const calculateTime = (secs) => {
    const minutes = Math.floor(secs / 60);
    const returnedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const seconds = Math.floor(secs % 60);
    const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
    return `${returnedMinutes}:${returnedSeconds}`;
  };

  const whilePlaying = useCallback(() => {
    if (audioPlayer) {
      progressBar.current.value = audioPlayer.current.currentTime;
      setPosition(progressBar.current.value);
      animationRef.current = requestAnimationFrame(whilePlaying);
    }
  }, []);

  const togglePlayPause = useCallback(() => {
    const prevValue = isPlaying;
    setIsPlaying(!prevValue);
    if (!prevValue) {
      audioPlayer.current.play();
      animationRef.current = requestAnimationFrame(whilePlaying);
    } else {
      audioPlayer.current.pause();
      cancelAnimationFrame(animationRef.current);
    }
  }, [isPlaying, whilePlaying]);

  // go back to start when reached the end
  useEffect(() => {
    if (Number.isNaN(position)) {
      setPosition(0);
    } else if (position !== 0 && position === duration) {
      togglePlayPause();
      setPosition(0);
      progressBar.current.value = 0;
    }
  }, [position, duration, togglePlayPause]);

  const changeRange = (value) => {
    if (!Number.isNaN(value)) {
      audioPlayer.current.currentTime = value;
      setPosition((audioPlayer.current.currentTime));
    }
  };

  return (
    <StyledContainer>
      <audio ref={audioPlayer} src={src} type="audio/mpeg" />
      <IconButton onClick={togglePlayPause}>
        {!isPlaying ? (
          <StyledPlayButton />
        ) : (
          <StyledPauseButton />
        )}
      </IconButton>
      <StyledSlider
        ref={progressBar}
        value={position}
        min={0}
        step={1}
        max={duration}
        onChange={(_, value) => changeRange(value)}
      />
      <StyledCounter>
        {position ? calculateTime(position) : '0:00'}
        {' '}
        /
        {' '}
        {duration ? calculateTime(duration) : '0:00'}
      </StyledCounter>
    </StyledContainer>
  );
}

export default AudioPlayer;

// Styling
const StyledContainer = styled(Box, {})({
  position: 'relative',
  alignSelf: 'flex-start',

  display: 'flex',
  margin: '25px 0 35px 0',
  width: '100%',

  alignItems: 'center',

  '& .MuiIconButton-root': {
    marginRight: 20,
    padding: 0,

    background: 'transparent',

    transition: `${theme.transitions.create(['background'], {
      duration: theme.transitions.duration.standard,
    })}`,
  },
});

const StyledPlayButton = styled(PlayCircleOutline, {})({
  opacity: 1,
  width: 40,
  height: 40,

  color: theme.palette.fontgrey,

  transition: 'opacity 250ms ease-in-out',

  '&:hover': {
    opacity: 0.8,

    transition: 'opacity 250ms ease-in-out',
  },
});

const StyledPauseButton = styled(PauseCircleOutline, {})({
  opacity: 1,
  width: 40,
  height: 40,

  color: theme.palette.fontgrey,

  transition: 'opacity 250ms ease-in-out',

  '&:hover': {
    opacity: 0.8,

    transition: 'opacity 250ms ease-in-out',
  },
});

const StyledSlider = styled(Slider, {})({
  width: '100%',
  height: 3,

  color: theme.palette.placeholdergrey,

  '& .MuiSlider-thumb': {
    marginTop: -1,
    width: 12,
    height: 12,

    background: theme.palette.newSecondary,
  },
});

const StyledCounter = styled(Typography, {})({
  position: 'absolute',
  right: 5,
  bottom: -10,

  color: theme.palette.placeholdergrey,
  fontFamily: 'Poppins Light',
  fontSize: 14,
  lineHeight: '17px',
});
